import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Alert,
    CircularProgress,
} from '@mui/material';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AuthLayout from '../../../components/AuthLayout';
import { useAppTheme } from '../../../App';
import { COLORS, SHADOWS, PRIMARY_ORANGE } from '../../../common/colors';
import { UserProfileService } from '../../user/services/userProfileService';
import { getUserDocument } from '../../../services/userService';
import { getAuth } from 'firebase/auth';

const TouristVisa: React.FC = () => {
    const { isDarkMode, getThemeStyles } = useAppTheme();
    const theme = getThemeStyles();
    const [isLoading, setIsLoading] = useState(false);
    const [isSignedUp, setIsSignedUp] = useState(false);
    const [isCheckingStatus, setIsCheckingStatus] = useState(true);
    const [notification, setNotification] = useState<{
        type: 'success' | 'error';
        message: string;
    } | null>(null);

    // Check user's current notification preference on component mount
    useEffect(() => {
        const checkNotificationStatus = async () => {
            try {
                const auth = getAuth();
                const user = auth.currentUser;

                if (user) {
                    const userData = await getUserDocument(user.uid);
                    if (userData?.notifyOnTouristVisa === true) {
                        setIsSignedUp(true);
                    }
                }
            } catch (error) {
                console.error('Error checking notification status:', error);
            } finally {
                setIsCheckingStatus(false);
            }
        };

        checkNotificationStatus();
    }, []);

    const handleNotifyMe = async () => {
        setIsLoading(true);
        setNotification(null);

        try {
            const response = await UserProfileService.updateTouristVisaNotificationPreference(true);

            if (response.status === 'success') {
                setIsSignedUp(true);
                setNotification({
                    type: 'success',
                    message: response.message
                });
            } else {
                setNotification({
                    type: 'error',
                    message: response.message
                });
            }
        } catch (error) {
            setNotification({
                type: 'error',
                message: 'Failed to update notification preference. Please try again.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const features = [
        {
            icon: <CheckCircleIcon sx={{ color: '#10b981', fontSize: '1.5rem' }} />,
            title: 'Visa Requirements Analysis',
            description: 'Get personalized requirements based on your nationality and destination'
        },
        {
            icon: <CheckCircleIcon sx={{ color: '#10b981', fontSize: '1.5rem' }} />,
            title: 'Document Checklist',
            description: 'Complete checklist of required documents with templates and examples'
        },
        {
            icon: <CheckCircleIcon sx={{ color: '#10b981', fontSize: '1.5rem' }} />,
            title: 'Application Guidance',
            description: 'Step-by-step guidance through the entire application process'
        },
        {
            icon: <CheckCircleIcon sx={{ color: '#10b981', fontSize: '1.5rem' }} />,
            title: 'AI-Powered Assistance',
            description: 'Smart recommendations and answers to your specific questions'
        }
    ];

    return (
        <AuthLayout>
            <Box
                sx={{
                    minHeight: 'calc(100vh - 64px)',
                    background: theme.background,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 4,
                    px: 2,
                }}
            >
                <Container maxWidth="md">
                    <Box
                        sx={{
                            textAlign: 'center',
                        }}
                    >
                        {/* Main Coming Soon Card */}
                        <Card
                            sx={{
                                borderRadius: 4,
                                background: theme.cardBackground,
                                boxShadow: `${SHADOWS.card} ${theme.shadowColor}`,
                                backdropFilter: 'blur(16px)',
                                border: `1px solid ${theme.borderColor}`,
                                mb: 4,
                            }}
                            elevation={0}
                        >
                            <CardContent sx={{ p: 6 }}>
                                {/* Icon and Title */}
                                <Box sx={{ mb: 5 }}>
                                    <Box
                                        sx={{
                                            width: 100,
                                            height: 100,
                                            borderRadius: '50%',
                                            backgroundColor: COLORS.primary,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 3,
                                            boxShadow: SHADOWS.primary,
                                        }}
                                    >
                                        <FlightTakeoffIcon sx={{ fontSize: '2.5rem', color: 'white' }} />
                                    </Box>

                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 700,
                                            color: theme.textPrimary,
                                            mb: 2,
                                            fontSize: { xs: '1.75rem', sm: '2.125rem' },
                                        }}
                                    >
                                        Tourist Visa Assistant
                                    </Typography>

                                    <Chip
                                        icon={<AccessTimeIcon sx={{ fontSize: '1rem' }} />}
                                        label="Coming Soon"
                                        sx={{
                                            background: 'rgba(251, 191, 36, 0.1)',
                                            color: '#f59e0b',
                                            border: '1px solid rgba(251, 191, 36, 0.2)',
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                            px: 2,
                                            py: 0.5,
                                            height: 32,
                                        }}
                                    />
                                </Box>

                                {/* Description */}
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.textSecondary,
                                        lineHeight: 1.6,
                                        mb: 5,
                                        maxWidth: 500,
                                        mx: 'auto',
                                        fontSize: '1.1rem',
                                    }}
                                >
                                    We&apos;re working hard to bring you the most comprehensive Tourist Visa assistance platform.
                                    Get ready for AI-powered guidance that will make your visa application process smooth and stress-free.
                                </Typography>

                                {/* Features Grid */}
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                        gap: 2.5,
                                        mb: 5,
                                    }}
                                >
                                    {features.map((feature, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                textAlign: 'left',
                                                p: 2.5,
                                                borderRadius: 2,
                                                background: isDarkMode
                                                    ? 'rgba(64, 65, 79, 0.2)'
                                                    : 'rgba(247, 247, 248, 0.6)',
                                                border: `1px solid ${theme.borderColor}`,
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-1px)',
                                                    background: isDarkMode
                                                        ? 'rgba(64, 65, 79, 0.3)'
                                                        : 'rgba(247, 247, 248, 0.9)',
                                                },
                                            }}
                                        >
                                            <Box sx={{ mr: 2, mt: 0.25 }}>
                                                {feature.icon}
                                            </Box>
                                            <Box>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: theme.textPrimary,
                                                        mb: 0.5,
                                                        fontSize: '0.9rem',
                                                    }}
                                                >
                                                    {feature.title}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: theme.textSecondary,
                                                        lineHeight: 1.4,
                                                        fontSize: '0.85rem',
                                                    }}
                                                >
                                                    {feature.description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>

                                {/* Notification Alert */}
                                {notification && (
                                    <Alert
                                        severity={notification.type}
                                        sx={{
                                            mb: 3,
                                            borderRadius: 2,
                                        }}
                                        onClose={() => setNotification(null)}
                                    >
                                        {notification.message}
                                    </Alert>
                                )}

                                {/* Notify Me Button */}
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={isLoading ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : isSignedUp ? (
                                        <CheckCircleIcon sx={{ fontSize: '1.1rem' }} />
                                    ) : isCheckingStatus ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        <NotificationsIcon sx={{ fontSize: '1.1rem' }} />
                                    )}
                                    disabled={isLoading || isSignedUp || isCheckingStatus}
                                    sx={{
                                        background: isSignedUp
                                                                            ? '#10b981'
                                : COLORS.primary,
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        px: 3,
                                        py: 1.25,
                                        borderRadius: 2,
                                        boxShadow: isSignedUp
                                            ? '0 4px 12px rgba(16, 185, 129, 0.25)'
                                            : SHADOWS.primary,
                                        textTransform: 'none',
                                        '&:hover': {
                                            background: isSignedUp
                                                                                ? '#059669'
                                : COLORS.primaryHover,
                                            transform: (isLoading || isSignedUp || isCheckingStatus) ? 'none' : 'translateY(-1px)',
                                            boxShadow: isSignedUp
                                                ? '0 4px 12px rgba(16, 185, 129, 0.25)'
                                                : '0 6px 20px rgba(59, 130, 246, 0.25)',
                                        },
                                        '&:disabled': {
                                            background: isSignedUp
                                                ? '#10b981'
                                                : theme.borderColor,
                                            color: isSignedUp ? 'white' : theme.textSecondary,
                                            transform: 'none',
                                            opacity: isSignedUp ? 1 : 0.6,
                                        },
                                    }}
                                    onClick={handleNotifyMe}
                                >
                                    {isCheckingStatus
                                        ? 'Checking status...'
                                        : isLoading
                                            ? 'Setting up notifications...'
                                            : isSignedUp
                                                ? 'You\'re all set! We\'ll notify you'
                                                : 'Notify Me When Available'
                                    }
                                </Button>
                            </CardContent>
                        </Card>

                        {/* Additional Info */}
                        <Box
                            sx={{
                                textAlign: 'center',
                                p: 3,
                                borderRadius: 3,
                                background: isDarkMode
                                    ? 'rgba(64, 65, 79, 0.2)'
                                    : 'rgba(247, 247, 248, 0.6)',
                                border: `1px solid ${theme.borderColor}`,
                            }}
                        >
                            <Typography
                                variant="body1"
                                sx={{
                                    color: theme.textSecondary,
                                    mb: 2,
                                    fontWeight: 500,
                                }}
                            >
                                In the meantime, try our{' '}
                                <Typography
                                    component="span"
                                    sx={{
                                        color: PRIMARY_ORANGE.main,
                                        fontWeight: 600,
                                    }}
                                >
                                    Global Talent Visa Assistant
                                </Typography>
                                {' '}for comprehensive visa guidance!
                            </Typography>

                            <Typography
                                variant="caption"
                                sx={{
                                    color: theme.textSecondary,
                                    opacity: 0.8,
                                }}
                            >
                                Expected launch: Q4 2025
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Box>
        </AuthLayout>
    );
};

export default TouristVisa; 